import { useMutation } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { produce } from "immer";
import { AxiosError, AxiosResponse } from "axios";

import { pluralizeNoun } from "shared/lib/helpers/utils";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";

import {
  AddColumnRequestI,
  AddFilterRequestI,
  AddSortRequestParamsI,
  BulkDisqualifyContactsFromListRequestI,
  BulkRemoveContactsFromListRequestI,
  ContactToListI,
  CreateOrUpdateListRequestParamsI,
  GetListsResponseI,
  LoadNewAssignedLeadsRequestI,
  UpdateCellRequestI,
  UpdateColumnRequestParamsI,
  UpdateFilterRequestI,
  UpdateSortRequestParamsI,
} from "@/api/routes/list";
import { useApiClient } from "@/context/api-client";
import { handleInfoMessage } from "@/helpers/error";
import { ERROR_CODES } from "@/constants/errors";
import { glencocoClientAPI } from "@/api/glencoco";
import {
  getListContactsQueryKey,
  listWorkspaceQueryKeys,
} from "@/modules/list-workspace/queries/keys";
import { useListsStore } from "@/modules/list-workspace/store/lists-store";
import { LISTS_QUERY_KEY } from "@/modules/list-workspace/queries/keys";
import { USER_ALREADY_HAS_LEADS_MODAL_ID } from "@/components/modals/lists/pull-leads/user-already-has-leads";

export const useQueryUpdateList = (listId: string) => {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: (parameters: CreateOrUpdateListRequestParamsI) =>
      api.updateList(listId, parameters),
    onSuccess: (_, parameters) => {
      void queryClient.setQueryData(
        listWorkspaceQueryKeys.lists(),
        (oldData: AxiosResponse<GetListsResponseI, any>) =>
          produce(oldData, (draft) => {
            draft.data.user_lists.forEach((list) => {
              if (list.id === listId) {
                list.name = parameters.name;
                list.description = parameters.description || "";
              }
            });
          })
      );
    },
    onError: () => toast.error("Failed to update this list."),
  });
};

export const useQueryCreateList = () => {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: (parameters: CreateOrUpdateListRequestParamsI) =>
      api.createList(parameters),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.lists(),
      });

      // This is for the lists v1 experience to work.
      // Remove this once we are ready to remove the v1 lists.
      queryClient.invalidateQueries({
        queryKey: [LISTS_QUERY_KEY],
      });
    },
    onError: ({ message }) =>
      toast.error("Failed to create new list. " + message),
  });
};

export const useQueryDeleteList = () => {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: (listId: string) => api.deleteList(listId),
    onSuccess: (_, listId) => {
      void queryClient.setQueryData(
        listWorkspaceQueryKeys.lists(),
        (oldData: AxiosResponse<GetListsResponseI, any>) =>
          produce(oldData, (draft) => {
            draft.data.user_lists = draft.data.user_lists.filter(
              (list) => list.id !== listId
            );
          })
      );
    },
    onError: ({ message }) => toast.error("Failed to delete list. " + message),
  });
};

export const useQueryCreateColumn = (viewId: string) => {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: (params: AddColumnRequestI) => api.addColumn(viewId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.availableColumns(),
      });
      //   TOOD: This needs to be tested/tweaked when we are ready to continue this feature
      //   void queryClient.setQueryData(
      //     listWorkspaceQueryKeys.availableColumns(),
      //     (oldData: AxiosResponse<GetAvailableColumnsResponseI, any>) =>
      //       produce(oldData, (draft) => {
      //         draft.data.column_types.push(resp.data);
      //       })
      //   );
    },
    onError: ({ message }) => toast.error("Failed to add column. " + message),
  });
};

export const useQueryUpdateColumn = (viewId: string) => {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: ({
      columnId,
      params,
    }: {
      columnId: string;
      params: UpdateColumnRequestParamsI;
    }) => api.updateColumn(viewId, columnId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.view(viewId),
      });
    },
    onError: ({ message }) =>
      toast.error("Failed to update column. " + message),
  });
};

export const useQueryAddFilter = (viewId: string) => {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: (params: AddFilterRequestI) => api.addFilter(viewId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.view(viewId),
      });
    },
    onError: ({ message }) => toast.error("Failed to add filter. " + message),
  });
};

export const useQueryUpdateFilter = (viewId: string) => {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: ({
      filterId,
      params,
    }: {
      filterId: string;
      params: UpdateFilterRequestI;
    }) => api.updateFilter(viewId, filterId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.view(viewId),
      });
    },
    onError: ({ message }) =>
      toast.error("Failed to update filter. " + message),
  });
};

export const useQueryDeleteFilter = (viewId: string) => {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: (filterId: string) => api.deleteFilter(viewId, filterId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.view(viewId),
      });
    },
    onError: ({ message }) =>
      toast.error("Failed to delete filter. " + message),
  });
};

export const useQueryUpdateCell = () => {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: (requestParams: UpdateCellRequestI) =>
      api.updateCell(requestParams),
    onSuccess: (_response, requestParams) => {
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.rows(requestParams.routeParams.viewId),
      });
    },
    onError: ({ message }) => toast.error("Failed to update cell. " + message),
  });
};

export const useQueryAddSort = (viewId: string) => {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: (params: AddSortRequestParamsI) => api.addSort(viewId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.view(viewId),
      });
    },
    onError: ({ message }) => toast.error("Failed to add sort. " + message),
  });
};

export const useQueryUpdateSort = (viewId: string) => {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: ({
      sortId,
      params,
    }: {
      sortId: string;
      params: UpdateSortRequestParamsI;
    }) => api.updateSort(viewId, sortId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.view(viewId),
      });
    },
    onError: ({ message }) => toast.error("Failed to update sort. " + message),
  });
};

export const useQueryDeleteSort = (viewId: string) => {
  const queryClient = useQueryClient();
  const api = useApiClient();

  return useMutation({
    mutationFn: (sortId: string) => api.deleteSort(viewId, sortId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.view(viewId),
      });
    },
    onError: ({ message }) => toast.error("Failed to delete sort. " + message),
  });
};

export const useWithStore_QueryBulkRemoveContactsFromList = (
  listId: string,
  viewId: string
) => {
  const queryClient = useQueryClient();
  const api = useApiClient();

  const subSection = useListsStore((state) => state.activeSubsection);
  const searchTerm = useListsStore((state) => state.searchTerm);
  const setSelectedRows = useListsStore((state) => state.setSelectedRows);

  return useMutation({
    mutationFn: ({ membership_ids }: BulkRemoveContactsFromListRequestI) =>
      api.bulkRemoveContactsFromListRequest(listId, {
        membership_ids,
      }),
    onSuccess: () => {
      // to update the counts in the lists dropdown
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.lists(),
      });

      // to update the counts in the cold/nurture switch
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.view(viewId),
      });

      // to update the table rows
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.rows(viewId, {
          list_subsection: subSection,
          search_term: searchTerm,
        }),
      });

      //clean up selected rows
      setSelectedRows([]);
    },
    onError: ({ message }) =>
      toast.error("Failed to remove contacts from this list." + message),
  });
};

export const useWithStore_QueryBulkAddContactsToList = (viewId: string) => {
  const queryClient = useQueryClient();
  const api = useApiClient();

  const subSection = useListsStore((state) => state.activeSubsection);
  const searchTerm = useListsStore((state) => state.searchTerm);

  return useMutation({
    mutationFn: ({
      contacts,
      list_ids,
    }: {
      contacts: ContactToListI[];
      list_ids: string[];
    }) =>
      api.bulkAddContactsToListRequest({
        contacts,
        list_ids,
      }),
    onSuccess: (_, { contacts: { length }, list_ids: listIds }) => {
      toast.success(
        `${
          length > 1 ? "Contacts have" : "Contact has"
        } been added to the specified ${pluralizeNoun("list", length)}`
      );

      // to update the counts
      listIds.forEach((listId) => {
        queryClient.invalidateQueries({
          queryKey: listWorkspaceQueryKeys.list(listId),
        });
      });

      // to update the table rows
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.rows(viewId, {
          list_subsection: subSection,
          search_term: searchTerm,
        }),
      });
    },
    onError: (_, { contacts: { length } }) => {
      toast.error(
        `Failed to add ${pluralizeNoun(
          "contact",
          length
        )} to the specified ${pluralizeNoun("list", length)}`
      );
    },
  });
};

export const useWithStore_QueryBulkDisqualifyContactsFromList = (
  listId: string,
  viewId: string
) => {
  const queryClient = useQueryClient();
  const api = useApiClient();

  const subSection = useListsStore((state) => state.activeSubsection);
  const searchTerm = useListsStore((state) => state.searchTerm);
  const setSelectedRows = useListsStore((state) => state.setSelectedRows);

  return useMutation({
    mutationFn: ({
      membership_ids,
      reason,
    }: BulkDisqualifyContactsFromListRequestI) =>
      api.bulkDisqualifyContactsFromListRequest(listId, {
        membership_ids,
        reason,
      }),
    onSuccess: () => {
      // to update the counts in the lists dropdown
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.lists(),
      });

      // to update the counts in the cold/nurture switch
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.view(viewId),
      });

      // to update the table rows
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.rows(viewId, {
          list_subsection: subSection,
          search_term: searchTerm,
        }),
      });

      //clean up selected rows
      setSelectedRows([]);
    },
    onError: ({ message }) =>
      toast.error("Failed to disqualify contacts from list." + message),
  });
};

export const useWithStore_useQueryLoadNewAssignedLeads = (
  viewId: string,
  listId: string
) => {
  const queryClient = useQueryClient();
  const api = useApiClient();

  const subSection = useListsStore((state) => state.activeSubsection);
  const searchTerm = useListsStore((state) => state.searchTerm);
  const setAvailableToCallColdLeadsForUser = useListsStore(
    (state) => state.setAvailableToCallColdLeadsForUser
  );

  return useMutation({
    mutationFn: (params: LoadNewAssignedLeadsRequestI) =>
      api.loadNewAssignedLeads(listId, params),
    onSuccess: () => {
      // to update the counts
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.view(viewId),
      });

      // to update the table rows
      queryClient.invalidateQueries({
        queryKey: listWorkspaceQueryKeys.rows(viewId, {
          list_subsection: subSection,
          search_term: searchTerm,
        }),
      });
    },
    onError: (err: AxiosError) => {
      // oxlint-disable-next-line no-unsafe-optional-chaining -- Pre-existing. A fix changes the error path, so it needs its own change.
      const errorCode = (err?.response?.data as any).error_code as number;
      const errorData = err?.response?.data as any;

      if (errorCode === ERROR_CODES.USER_ALREADY_HAS_LEADS) {
        setAvailableToCallColdLeadsForUser(
          errorData.available_to_call_cold_leads_for_user || []
        );

        modalHelpers.open(USER_ALREADY_HAS_LEADS_MODAL_ID);
      } else {
        handleInfoMessage(errorCode, "No more leads available.");
      }
    },
  });
};

export const useQueryFetchColumnSelectOptions = () => {
  const api = useApiClient();

  return useMutation({
    mutationFn: (columnTypeId: string) =>
      api.getListColumnSelectOptions(columnTypeId),
    onError: (err: AxiosError) => {
      toast.error(`Failed to fetch column select options. ${err.message}`);
    },
  });
};

export const useQueryFetchListRowsByAccountId = () => {
  const api = useApiClient();

  return useMutation({
    mutationFn: ({
      viewId,
      campaignId,
      accountId,
    }: {
      viewId: string;
      campaignId: string;
      accountId: string;
    }) => api.getListViewRowsByAccountId(viewId, campaignId, accountId),
    onError: (err: AxiosError) => {
      toast.error(`Failed to fetch list rows by account id. ${err.message}`);
    },
  });
};

export const useCreateList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (parameters: CreateOrUpdateListRequestParamsI) =>
      glencocoClientAPI().createList(parameters),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [LISTS_QUERY_KEY],
      }),
  });
};

export const useBulkAddContactsToList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contacts,
      list_ids,
    }: {
      contacts: ContactToListI[];
      list_ids: string[];
    }) =>
      glencocoClientAPI().bulkAddContactsToListRequest({
        contacts,
        list_ids,
      }),
    onSuccess: (_, { contacts: { length }, list_ids: listIds }) => {
      toast.success(
        `${
          length > 1 ? "Contacts have" : "Contact has"
        } been added to the specified ${pluralizeNoun("list", length)}`
      );

      listIds.forEach((listId) => {
        queryClient.invalidateQueries({
          queryKey: getListContactsQueryKey(listId),
        });
      });
    },
    onError: (_, { contacts: { length } }) => {
      toast.error(
        `Failed to add ${pluralizeNoun(
          "contact",
          length
        )} to the specified ${pluralizeNoun("list", length)}`
      );
    },
  });
};
